import { useState } from "react";
import { useDashboard, StatCard } from "../lib/useDashboard";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import WeeklyPlanPanel, { weekLabel } from "./WeeklyPlanPanel";
import AssignStaffDialog from "./AssignStaffDialog";
import AddStaffDialog from "./AddStaffDialog";
import StaffCaseloadDialog from "./StaffCaseloadDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Users, ClipboardList, FileText, LayoutDashboard, UserPlus, Target, AlertTriangle, ArrowRight, Sparkles, Pencil, KeyRound, History, Loader2 } from "lucide-react";

export default function QpDashboard({ onGoConsumers, onGoPcp, onWriteNote }) {
  const { data, loading, reload } = useDashboard();
  const [assignFor, setAssignFor] = useState(null);
  const [planFor, setPlanFor] = useState(null);
  const [addStaff, setAddStaff] = useState(false);
  const [planningAll, setPlanningAll] = useState(null);

  const planAll = async () => {
    const todo = consumers.filter((c) => !c.has_plan && c.goals_count > 0);
    if (!todo.length) { toast.info("Every consumer with PCP goals already has a plan this week."); return; }
    setPlanningAll({ done: 0, total: todo.length });
    let ok = 0;
    for (const c of todo) {
      try { await api.post("/weekly-plans/generate", { consumer_id: c.id, carry_forward: true }); ok += 1; } catch { /* keep going */ }
      setPlanningAll({ done: ok, total: todo.length });
    }
    setPlanningAll(null);
    toast.success(`Built this week's plan from last week for ${ok} of ${todo.length} consumers.`);
    reload();
  };
  const [staffFor, setStaffFor] = useState(null);
  const consumers = data?.consumers || [];
  const staff = data?.staff || [];
  const s = data?.stats || {};

  return (
    <div data-testid="qp-dashboard">
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white border border-[#E2DFD8] flex items-center justify-center">
            <LayoutDashboard className="w-5 h-5 text-[#4A6B53]" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-[#121814]">Caseload Dashboard</h1>
            <p className="text-sm text-[#7A8C82]">Assign consumers to staff and plan each week's interventions · {weekLabel(data?.week_start)}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="qp-plan-all-btn" onClick={planAll} disabled={!!planningAll} className="h-10 px-4 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] disabled:opacity-70 text-white text-sm font-medium flex items-center gap-2 transition-colors">
            {planningAll ? <><Loader2 className="w-4 h-4 animate-spin" /> Planning {planningAll.done}/{planningAll.total}…</> : <><History className="w-4 h-4" strokeWidth={1.5} /> Plan all from last week</>}
          </button>
          <button data-testid="qp-go-pcp-btn" onClick={onGoPcp} className="h-10 px-4 rounded-full bg-white border border-[#4A6B53] text-[#4A6B53] hover:bg-[#4A6B53] hover:text-white text-sm font-medium flex items-center gap-2 transition-colors">
            <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Create / upload PCP
          </button>
          <button data-testid="qp-go-consumers-btn" onClick={onGoConsumers} className="h-10 px-4 rounded-full bg-white border border-[#E2DFD8] text-[#3A453F] hover:border-[#4A6B53] text-sm font-medium flex items-center gap-2 transition-colors">
            View all consumers <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {loading && <p className="text-sm text-[#7A8C82]">Loading…</p>}

      {!loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard testid="qp-stat-consumers" icon={Users} label="Consumers" value={s.consumers ?? 0} onClick={onGoConsumers} />
            <StatCard testid="qp-stat-unassigned" icon={AlertTriangle} label="Unassigned" value={s.unassigned ?? 0} color={s.unassigned ? "#C26D5C" : "#4A6B53"} />
            <StatCard testid="qp-stat-plans" icon={ClipboardList} label="Weekly plans ready" value={`${s.plans_ready ?? 0}/${s.consumers ?? 0}`} color="#D4A373" />
            <StatCard testid="qp-stat-notes" icon={FileText} label="Team notes this week" value={s.notes_this_week ?? 0} />
          </div>

          <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 bg-white border border-[#E2DFD8] rounded-2xl shadow-sm overflow-hidden">
              <div className="px-5 py-3 border-b border-[#E2DFD8] bg-[#F9F9F6]">
                <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-[0.2em]">Consumers · assignments &amp; weekly plans</p>
              </div>
              {consumers.length === 0 && (
                <p className="p-6 text-sm text-[#7A8C82]" data-testid="qp-no-consumers">No consumers yet — create or upload a PCP to add one.</p>
              )}
              <ul className="divide-y divide-[#EEF2EF]">
                {consumers.map((c) => (
                  <li key={c.id} data-testid={`qp-consumer-row-${c.id}`} className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#121814] truncate">{c.name} <span className="text-xs font-normal text-[#7A8C82]">{c.member_id}</span></p>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {c.assigned_staff_names.length === 0 && (
                          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded bg-[#FBF2EF] text-[#B0503C]" data-testid={`unassigned-badge-${c.id}`}>Unassigned</span>
                        )}
                        {c.assigned_staff_names.map((n) => (
                          <span key={n} className="text-[11px] px-2 py-0.5 rounded-full bg-[#EAF1EC] text-[#3D5A45]">{n}</span>
                        ))}
                        <span className="text-[11px] text-[#7A8C82]">· {c.goals_count} goals · {c.notes_this_week} notes this wk</span>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`assign-btn-${c.id}`} onClick={() => setAssignFor(c)}
                        className="h-9 px-3 rounded-full bg-white border border-[#E2DFD8] text-[#3A453F] hover:border-[#4A6B53] text-xs font-medium flex items-center gap-1.5 transition-colors">
                        <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> Assign
                      </button>
                      <button data-testid={`plan-btn-${c.id}`} onClick={() => setPlanFor(c)}
                        className={`h-9 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${c.has_plan ? "bg-[#EAF1EC] text-[#3D5A45] hover:bg-[#DCE8DF]" : "bg-[#4A6B53] text-white hover:bg-[#3D5A45]"}`}>
                        <Target className="w-3.5 h-3.5" strokeWidth={1.5} /> {c.has_plan ? `Plan · ${c.plan_done ?? 0}/${c.plan_interventions} done` : "Plan week"}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-4 bg-white border border-[#E2DFD8] rounded-2xl p-5 shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-[10px] font-bold text-[#7A8C82] uppercase tracking-[0.2em] flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" strokeWidth={2} /> Staff caseloads
                </p>
                <button data-testid="add-staff-btn" onClick={() => setAddStaff(true)} className="h-8 px-3 rounded-full bg-[#4A6B53] hover:bg-[#3D5A45] text-white text-xs font-medium flex items-center gap-1.5 transition-colors">
                  <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} /> Add staff
                </button>
              </div>
              {staff.length === 0 && <p className="text-sm text-[#7A8C82]">No staff accounts yet — add your first PP, AP or QP.</p>}
              <div className="space-y-2">
                {staff.map((st) => (
                  <button key={st.id} type="button" data-testid={`staff-load-${st.id}`} onClick={() => setStaffFor(st)}
                    className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg bg-[#F9F9F6] border border-[#E2DFD8] hover:border-[#4A6B53] text-left transition-colors group">
                    <div className="min-w-0">
                      <p className="text-sm text-[#121814] truncate flex items-center gap-1.5">{st.name}
                        {st.must_change_password && <span title="Has not set their own password yet"><KeyRound className="w-3 h-3 text-[#D4A373]" strokeWidth={2} /></span>}
                      </p>
                      <p className="text-[11px] text-[#7A8C82]">{st.role.toUpperCase()}{st.credentials ? ` · ${st.credentials}` : ""}</p>
                    </div>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="font-display text-xl font-extrabold text-[#4A6B53]">{st.assigned_count}</span>
                      <Pencil className="w-3.5 h-3.5 text-[#B8C4BC] group-hover:text-[#4A6B53]" strokeWidth={1.5} />
                    </span>
                  </button>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-[#7A8C82]">Tap a staff member to edit their details and caseload.</p>
            </div>
          </div>
        </>
      )}

      <AssignStaffDialog consumer={assignFor} staff={staff} open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)} onSaved={reload} />
      <AddStaffDialog open={addStaff} onOpenChange={setAddStaff} onCreated={reload} />
      <StaffCaseloadDialog staff={staffFor} consumers={consumers} open={!!staffFor} onOpenChange={(o) => !o && setStaffFor(null)} onSaved={reload} />

      <Dialog open={!!planFor} onOpenChange={(o) => !o && setPlanFor(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qp-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{planFor?.name}</DialogTitle>
            <DialogDescription>Generate or review this week's interventions for staff.</DialogDescription>
          </DialogHeader>
          {planFor && (
            <WeeklyPlanPanel consumer={planFor} canGenerate onChanged={reload}
              onWriteNote={onWriteNote ? (c, g, i) => { setPlanFor(null); onWriteNote(c, g, i); } : undefined} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
